import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Container, Form, Button, Alert, Spinner, Card } from 'react-bootstrap';
import axios from 'axios';
import { API_CONFIG } from '../../config/env';
import { FIAT_CURRENCIES } from '../../constants/fiatCurrencies';
import { exchangeRateService, USD_MINIMUM } from '../../services/exchangeRateService';
import { AvailableProviderOption, MerchantReferences } from '../../services/paymentLinkService';

const RIZZPAY_LOGO_PUBLIC_PATH = '/rizzpay-mark.png';

type IframeTheme = 'light' | 'dark';

interface FiatCurrencyPresentation {
  code: string;
  label: string;
  countryCode: string;
  flagUrl: string;
  isPopular: boolean;
}

const POPULAR_FIAT_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF', 'JPY', 'AED', 'BRL', 'INR'];

const FIAT_CURRENCY_COUNTRY: Record<string, string> = {
  AED: 'ae',
  AFN: 'af',
  ALL: 'al',
  AMD: 'am',
  ANG: 'cw',
  AOA: 'ao',
  ARS: 'ar',
  AUD: 'au',
  AZN: 'az',
  BAM: 'ba',
  BBD: 'bb',
  BDT: 'bd',
  BGN: 'bg',
  BHD: 'bh',
  BMD: 'bm',
  BND: 'bn',
  BOB: 'bo',
  BRL: 'br',
  BSD: 'bs',
  BWP: 'bw',
  BYN: 'by',
  BZD: 'bz',
  CAD: 'ca',
  CHF: 'ch',
  CLP: 'cl',
  CNY: 'cn',
  COP: 'co',
  CRC: 'cr',
  CZK: 'cz',
  DJF: 'dj',
  DKK: 'dk',
  DOP: 'do',
  DZD: 'dz',
  EGP: 'eg',
  EUR: 'eu',
  GBP: 'gb',
  GEL: 'ge',
  GHS: 'gh',
  GTQ: 'gt',
  HKD: 'hk',
  HNL: 'hn',
  HUF: 'hu',
  IDR: 'id',
  ILS: 'il',
  INR: 'in',
  ISK: 'is',
  JMD: 'jm',
  JOD: 'jo',
  JPY: 'jp',
  KES: 'ke',
  KGS: 'kg',
  KHR: 'kh',
  KMF: 'km',
  KRW: 'kr',
  KWD: 'kw',
  KYD: 'ky',
  KZT: 'kz',
  LAK: 'la',
  LBP: 'lb',
  LKR: 'lk',
  LRD: 'lr',
  MAD: 'ma',
  MDL: 'md',
  MGA: 'mg',
  MKD: 'mk',
  MNT: 'mn',
  MOP: 'mo',
  MRU: 'mr',
  MUR: 'mu',
  MWK: 'mw',
  MXN: 'mx',
  MYR: 'my',
  MZN: 'mz',
  NAD: 'na',
  NGN: 'ng',
  NIO: 'ni',
  NOK: 'no',
  NPR: 'np',
  NZD: 'nz',
  OMR: 'om',
  PAB: 'pa',
  PEN: 'pe',
  PGK: 'pg',
  PHP: 'ph',
  PKR: 'pk',
  PLN: 'pl',
  PYG: 'py',
  QAR: 'qa',
  RON: 'ro',
  RSD: 'rs',
  RUB: 'ru',
  RWF: 'rw',
  SAR: 'sa',
  SCR: 'sc',
  SEK: 'se',
  SGD: 'sg',
  SLL: 'sl',
  SOS: 'so',
  THB: 'th',
  TJS: 'tj',
  TMT: 'tm',
  TND: 'tn',
  TRY: 'tr',
  TTD: 'tt',
  TWD: 'tw',
  TZS: 'tz',
  UAH: 'ua',
  UGX: 'ug',
  USD: 'us',
  UYU: 'uy',
  UZS: 'uz',
  VND: 'vn',
  XAF: 'cm',
  XCD: 'ag',
  XOF: 'sn',
  YER: 'ye',
  ZAR: 'za',
  ZMW: 'zm',
};

const FIAT_CURRENCY_LABELS: Record<string, string> = {
  AED: 'UAE dirham',
  AUD: 'Australian dollar',
  BRL: 'Brazilian real',
  CAD: 'Canadian dollar',
  CHF: 'Swiss franc',
  CNY: 'Chinese yuan',
  EUR: 'Euro',
  GBP: 'British pound',
  HKD: 'Hong Kong dollar',
  INR: 'Indian rupee',
  JPY: 'Japanese yen',
  MXN: 'Mexican peso',
  NZD: 'New Zealand dollar',
  SGD: 'Singapore dollar',
  USD: 'US dollar',
};

const resolveIframeTheme = (_raw: string | null): IframeTheme => 'dark';

const resolveFlagUrl = (countryCode: string): string => (
  `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`
);

const buildCurrencyPresentation = (code: string): FiatCurrencyPresentation => {
  const normalizedCode = code.trim().toUpperCase();
  const countryCode = FIAT_CURRENCY_COUNTRY[normalizedCode] ?? normalizedCode.slice(0, 2).toLowerCase();

  return {
    code: normalizedCode,
    label: FIAT_CURRENCY_LABELS[normalizedCode] ?? `${normalizedCode} currency`,
    countryCode,
    flagUrl: resolveFlagUrl(countryCode),
    isPopular: POPULAR_FIAT_CURRENCIES.includes(normalizedCode),
  };
};

interface MerchantDepositResponse {
  paymentId?: string;
  depositId: number;
  status: string;
  displayStatus: string;
  checkoutUrl: string;
  publicPaymentId: string;
  requestedAmount?: {
    amount: number;
    currency: string;
  };
  walletPayment: {
    asset: string;
    network: string;
    walletAddress: string;
    quotedAmount?: number | null;
    quoteExpiresAt?: string | null;
  };
  cardOrBankOptions?: Array<{
    checkoutSessionId: string;
    providerKey?: string;
    provider: string;
    paymentMethod: string;
    url: string;
    recommended?: boolean;
    expiresAt?: string;
  }>;
  merchantReferences?: MerchantReferences | null;
}

const extractApiErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message || fallbackMessage : fallbackMessage;
  }

  const responseData = error.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  const typedResponse = responseData as
    | { expandedError?: { technicalMessage?: string; message?: string }; error?: { message?: string }; message?: string }
    | undefined;

  return (
    typedResponse?.expandedError?.message ||
    typedResponse?.expandedError?.technicalMessage ||
    typedResponse?.error?.message ||
    typedResponse?.message ||
    error.message ||
    fallbackMessage
  );
};

const isProviderConfigurationOnlyError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const responseData = error.response?.data as
    | { expandedError?: { normalizedCode?: string; message?: string }; message?: string }
    | undefined;

  const normalizedCode = responseData?.expandedError?.normalizedCode?.toLowerCase();
  const message = (
    responseData?.expandedError?.message ||
    responseData?.message ||
    error.message ||
    ''
  ).toLowerCase();

  return normalizedCode === 'configuration_error'
    || message.includes('no fiat providers are configured')
    || message.includes('provider discovery is not configured');
};

const resolvePaymentPageUrl = (link?: string | null): string => {
  if (!link) {
    return '';
  }

  if (typeof window === 'undefined') {
    return link;
  }

  try {
    return new URL(link, window.location.origin).toString();
  } catch {
    return link;
  }
};

const buildEmbeddedCheckoutUrl = (link?: string | null): string => {
  const paymentPageUrl = resolvePaymentPageUrl(link);
  if (!paymentPageUrl) {
    return '';
  }

  return paymentPageUrl.includes('?')
    ? `${paymentPageUrl}&embedded=1`
    : `${paymentPageUrl}?embedded=1`;
};

const isTruthyParam = (raw: string | null): boolean => {
  if (!raw) {
    return false;
  }

  return ['1', 'true', 'yes', 'on', 'locked'].includes(raw.trim().toLowerCase());
};

const postParentMessage = (type: string, payload: unknown) => {
  if (typeof window === 'undefined' || window.self === window.top) {
    return;
  }

  window.parent.postMessage(
    {
      source: 'COR_PAYMENT',
      type,
      occurredAt: new Date().toISOString(),
      payload,
    },
    '*'
  );
};

const normalizeMerchantReferences = (references: MerchantReferences | null): MerchantReferences | null => {
  if (!references) {
    return null;
  }

  const metadataEntries = Object.entries(references.metadata ?? {})
    .filter(([key]) => key.trim().length > 0)
    .map(([key, value]) => [key.trim(), value == null || `${value}`.trim().length === 0 ? null : `${value}`.trim()] as const);

  const normalized: MerchantReferences = {
    merchantTransactionId: references.merchantTransactionId?.trim() || undefined,
    merchantUserId: references.merchantUserId?.trim() || undefined,
    merchantSessionId: references.merchantSessionId?.trim() || undefined,
    idempotencyKey: references.idempotencyKey?.trim() || undefined,
    metadata: metadataEntries.length > 0 ? Object.fromEntries(metadataEntries) : undefined,
  };

  const hasValues = Boolean(
    normalized.merchantTransactionId ||
    normalized.merchantUserId ||
    normalized.merchantSessionId ||
    normalized.idempotencyKey ||
    (normalized.metadata && Object.keys(normalized.metadata).length > 0)
  );

  return hasValues ? normalized : null;
};

const parseMerchantMetadata = (raw: string | null): Record<string, string | null> | undefined => {
  if (!raw || raw.trim().length === 0) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const entries = Object.entries(parsed)
      .filter(([key]) => key.trim().length > 0)
      .map(([key, value]) => [key.trim(), value == null ? null : `${value}`] as const);

    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  } catch {
    return undefined;
  }
};

const getSurfaceColor = (background: string) => {
  try {
    const hex = background.replace('#', '');
    if (hex.length !== 6) {
      return '#ffffff';
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    if ([r, g, b].some((value) => Number.isNaN(value))) {
      return '#ffffff';
    }

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128 ? '#ffffff' : '#f8f9fa';
  } catch {
    return '#ffffff';
  }
};

const IframePaymentPage: React.FC = () => {
  const { embedToken } = useParams<{ embedToken: string }>();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const iframeTheme = resolveIframeTheme(searchParams.get('theme'));
  const presetCurrency = (searchParams.get('currency') || 'USD').trim().toUpperCase();
  const requestedCurrency = FIAT_CURRENCIES.includes(presetCurrency) ? presetCurrency : 'USD';
  const requestedAmount = searchParams.get('amount')?.trim() || '';
  const isAmountLocked = isTruthyParam(searchParams.get('lockAmount'));
  const merchantReferences = useMemo(() => {
    return normalizeMerchantReferences({
      merchantTransactionId: searchParams.get('merchantTransactionId') ?? undefined,
      merchantUserId: searchParams.get('merchantUserId') ?? undefined,
      merchantSessionId: searchParams.get('merchantSessionId') ?? undefined,
      idempotencyKey: searchParams.get('idempotencyKey') ?? undefined,
      metadata: parseMerchantMetadata(searchParams.get('merchantMetadata')),
    });
  }, [searchParams]);

  const accentColor = '#0ea5e9';
  const accentStrongColor = '#2563eb';
  const accentSoftColor = '#38bdf8';

  const [fiatCurrency, setFiatCurrency] = useState(requestedCurrency);
  const [fiatAmount, setFiatAmount] = useState(requestedAmount);
  const [currencySearchTerm, setCurrencySearchTerm] = useState('');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<MerchantDepositResponse | null>(null);
  const [embeddedCheckoutLoaded, setEmbeddedCheckoutLoaded] = useState(false);
  const [embeddedCheckoutHeight, setEmbeddedCheckoutHeight] = useState(640);
  const [availableProviders, setAvailableProviders] = useState<AvailableProviderOption[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [, setProviderError] = useState<string | null>(null);
  const [canContinueWithoutFiatProviders, setCanContinueWithoutFiatProviders] = useState(false);
  const [minimumAmount, setMinimumAmount] = useState<number>(USD_MINIMUM);
  const [minimumAmountLoading, setMinimumAmountLoading] = useState<boolean>(false);

  const currencyDropdownRef = useRef<HTMLDivElement>(null);
  const embeddedCheckoutFrameRef = useRef<HTMLIFrameElement | null>(null);
  const autoStartAttemptedKeyRef = useRef<string | null>(null);

  const liveCheckoutMode = Boolean(successData);
  const effectiveIframeTheme: IframeTheme = liveCheckoutMode ? 'dark' : iframeTheme;
  const defaultBackgroundColor = effectiveIframeTheme === 'dark' ? '#08090d' : '#f4f7fb';
  const defaultFontColor = effectiveIframeTheme === 'dark' ? '#f8fbff' : '#0f172a';
  const backgroundColor = defaultBackgroundColor;
  const fontColor = defaultFontColor;

  useEffect(() => {
    postParentMessage('COR_IFRAME_READY', {
      embedTokenPresent: Boolean(embedToken),
      merchantReferences,
    });
  }, [embedToken, merchantReferences]);

  const currencyOptions = useMemo(
    () => FIAT_CURRENCIES.map(buildCurrencyPresentation),
    []
  );
  const popularCurrencyOptions = useMemo(
    () => POPULAR_FIAT_CURRENCIES
      .filter((currency) => FIAT_CURRENCIES.includes(currency))
      .map(buildCurrencyPresentation),
    []
  );
  const selectedCurrencyOption = useMemo(
    () => buildCurrencyPresentation(fiatCurrency),
    [fiatCurrency]
  );
  const filteredCurrencyOptions = useMemo(() => {
    const query = currencySearchTerm.trim().toLowerCase();

    if (!query) {
      return currencyOptions;
    }

    return currencyOptions.filter((currency) => (
      currency.code.toLowerCase().includes(query) ||
      currency.label.toLowerCase().includes(query) ||
      currency.countryCode.toLowerCase().includes(query)
    ));
  }, [currencyOptions, currencySearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target as Node)) {
        setShowCurrencyDropdown(false);
      }
    };

    if (showCurrencyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCurrencyDropdown]);

  useEffect(() => {
    const fetchMinimumAmount = async () => {
      setMinimumAmountLoading(true);
      try {
        let min: number;
        try {
          const response = await axios.get<{ minimumFiatAmount?: number }>(
            `${API_CONFIG.BASE_URL}/merchant/deposits/minimum-fiat-amount`,
            {
              params: { fiatCurrency },
              headers: {
                'X-COR-Embed-Token': embedToken,
              },
            }
          );

          min = typeof response.data?.minimumFiatAmount === 'number'
            ? response.data.minimumFiatAmount
            : await exchangeRateService.getMinimumAmount(fiatCurrency);
        } catch {
          min = await exchangeRateService.getMinimumAmount(fiatCurrency);
        }

        setMinimumAmount(min);
      } catch (fetchError) {
        console.error('Error fetching minimum amount:', fetchError);
        setMinimumAmount(USD_MINIMUM);
      } finally {
        setMinimumAmountLoading(false);
      }
    };

    if (fiatCurrency && embedToken) {
      void fetchMinimumAmount();
    }
  }, [embedToken, fiatCurrency]);

  useEffect(() => {
    if (!embedToken) {
      return;
    }

    const amount = parseFloat(fiatAmount);
    if (!fiatCurrency || Number.isNaN(amount) || amount < minimumAmount) {
      setAvailableProviders([]);
      setProviderError(null);
      setCanContinueWithoutFiatProviders(false);
      return;
    }

    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setProvidersLoading(true);
      setProviderError(null);
      setCanContinueWithoutFiatProviders(false);

      try {
        const response = await axios.post<AvailableProviderOption[]>(
          `${API_CONFIG.BASE_URL}/merchant/deposits/provider-options`,
          {
            fiatCurrency,
            amount,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'X-COR-Embed-Token': embedToken,
            },
          }
        );

        if (cancelled) {
          return;
        }

        setAvailableProviders(response.data);
        setProviderError(null);
        setCanContinueWithoutFiatProviders(response.data.length === 0);
      } catch (providerFetchError) {
        console.error('Failed to load iframe providers', providerFetchError);
        if (!cancelled) {
          const canFallbackToDirectCryptoOnly = isProviderConfigurationOnlyError(providerFetchError);
          setAvailableProviders([]);
          setCanContinueWithoutFiatProviders(canFallbackToDirectCryptoOnly);
          setProviderError(
            canFallbackToDirectCryptoOnly
              ? 'Fiat checkout is not available right now. You can still continue and pay with the wallet option on the next page.'
              : extractApiErrorMessage(providerFetchError, 'We could not check fiat checkout availability right now.')
          );
        }
      } finally {
        if (!cancelled) {
          setProvidersLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [embedToken, fiatAmount, fiatCurrency, minimumAmount]);

  const handleCurrencySelect = (currency: string) => {
    if (successData) {
      setSuccessData(null);
      setEmbeddedCheckoutLoaded(false);
    }

    setFiatCurrency(currency);
    setCurrencySearchTerm('');
    setShowCurrencyDropdown(false);
  };

  const handleCurrencySearchChange = (value: string) => {
    if (successData) {
      setSuccessData(null);
      setEmbeddedCheckoutLoaded(false);
    }

    setCurrencySearchTerm(value);
    setShowCurrencyDropdown(true);
  };

  const handleAmountChange = (value: string) => {
    if (successData) {
      setSuccessData(null);
      setEmbeddedCheckoutLoaded(false);
    }

    setFiatAmount(value);
  };

  const createPaymentPage = useCallback(async () => {
    setError(null);
    setSuccessData(null);
    setEmbeddedCheckoutLoaded(false);

    if (!embedToken) {
      setError('This embed link is missing an embed token.');
      return false;
    }

    const amount = parseFloat(fiatAmount);
    const isAmountBelowMinimum = !Number.isNaN(amount) && amount < minimumAmount;
    if (!fiatAmount || Number.isNaN(amount) || amount < minimumAmount) {
      const minFormatted = exchangeRateService.formatMinimum(fiatCurrency, minimumAmount);
      setError(
        isAmountLocked
          ? `This checkout amount is below the minimum. Ask the merchant to refresh the payment with at least ${minFormatted}.`
          : `Enter a valid amount. The minimum to continue is ${minFormatted}.`
      );
      return false;
    }

    const previewProviderKeys = availableProviders.map((provider) => provider.providerKey);

    if (previewProviderKeys.length === 0 && !(canContinueWithoutFiatProviders && !isAmountBelowMinimum)) {
      setError('This payment cannot open a checkout yet. Try another amount or try again when fiat checkout is available.');
      return false;
    }

    setLoading(true);

    try {
      const payload = {
        fiatCurrency,
        amount,
        merchantReferences: merchantReferences ?? undefined,
      };

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-COR-Embed-Token': embedToken,
      };

      const response = await axios.post<MerchantDepositResponse>(
        `${API_CONFIG.BASE_URL}/merchant/deposits`,
        payload,
        { headers }
      );

      setSuccessData(response.data);
      postParentMessage('COR_PAYMENT_PAGE_CREATED', {
        paymentId: response.data.paymentId ?? response.data.publicPaymentId,
        transactionId: response.data.paymentId ?? response.data.publicPaymentId,
        paymentPageUrl: response.data.checkoutUrl,
        checkoutUrl: response.data.checkoutUrl,
        uniqueWalletAddress: response.data.walletPayment.walletAddress,
        disposableAddress: response.data.walletPayment.walletAddress,
        walletPayment: response.data.walletPayment,
        cardOrBankOptions: response.data.cardOrBankOptions ?? [],
        lifecycleStatus: response.data.status,
        displayStatus: response.data.displayStatus,
        selectedProviders: (response.data.cardOrBankOptions ?? [])
          .map((option) => option.providerKey || option.provider)
          .filter((value): value is string => Boolean(value)),
        previewProviderKeys,
        merchantReferences: response.data.merchantReferences ?? merchantReferences,
      });
      return true;
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? typeof err.response?.data === 'string'
          ? err.response.data
          : err.response?.data?.message || 'Something went wrong, try again later.'
        : 'Something went wrong, try again later.';

      setError(message);
      postParentMessage('COR_PAYMENT_ERROR', { message, merchantReferences });
      return false;
    } finally {
      setLoading(false);
    }
  }, [
    availableProviders,
    canContinueWithoutFiatProviders,
    embedToken,
    fiatAmount,
    fiatCurrency,
    isAmountLocked,
    merchantReferences,
    minimumAmount,
  ]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await createPaymentPage();
  };

  const handleResetCheckout = () => {
    setError(null);
    setSuccessData(null);
    setEmbeddedCheckoutLoaded(false);
    autoStartAttemptedKeyRef.current = null;
  };

  const surfaceColor = effectiveIframeTheme === 'dark' ? '#12141f' : getSurfaceColor(backgroundColor);
  const borderColor = effectiveIframeTheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.12)';
  const dividerColor = effectiveIframeTheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)';
  const softColor = effectiveIframeTheme === 'dark' ? 'rgba(14, 165, 233, 0.08)' : 'rgba(15, 23, 42, 0.035)';
  const parsedAmount = parseFloat(fiatAmount);
  const hasEnteredAmount = !Number.isNaN(parsedAmount);
  const isBelowMinimum = hasEnteredAmount && parsedAmount < minimumAmount;
  const hasAvailableCheckoutPath = availableProviders.length > 0 || (canContinueWithoutFiatProviders && hasEnteredAmount && !isBelowMinimum);
  const embeddedCheckoutUrl = buildEmbeddedCheckoutUrl(successData?.checkoutUrl || '');
  const shopMode = isAmountLocked;
  const lockedAmountReady = shopMode && hasEnteredAmount && !isBelowMinimum;
  const autoStartKey = shopMode && lockedAmountReady ? `${fiatCurrency}:${fiatAmount}` : null;
  const setupIssue = !embedToken
    ? 'This embed link is missing an embed token, so it cannot create payment pages yet.'
    : null;
  const amountIssue = shopMode && !hasEnteredAmount
    ? 'This checkout is missing its preset amount. Ask the merchant to refresh the page.'
    : null;
  const successLink = resolvePaymentPageUrl(successData?.checkoutUrl || '');
  const primaryHeading = shopMode ? 'Complete your payment' : 'Choose an amount and pay';
  const syncEmbeddedCheckoutHeight = useCallback(() => {
    const frame = embeddedCheckoutFrameRef.current;
    if (!frame) {
      return;
    }

    try {
      const doc = frame.contentDocument || frame.contentWindow?.document;
      if (!doc) {
        return;
      }

      const body = doc.body;
      const bodyHeight = Math.max(
        body?.scrollHeight ?? 0,
        body?.offsetHeight ?? 0,
        body?.getBoundingClientRect?.().height ?? 0
      );
      const fallbackHeight = Math.max(
        doc.documentElement?.scrollHeight ?? 0,
        doc.documentElement?.offsetHeight ?? 0
      );
      const nextHeight = bodyHeight > 0 ? bodyHeight : fallbackHeight;

      if (nextHeight > 0) {
        setEmbeddedCheckoutHeight(Math.max(420, Math.min(2800, nextHeight + 16)));
      }
    } catch {
      // Same-page checkout is expected to be same-origin, but fail safely if that changes.
    }
  }, []);

  useEffect(() => {
    if (!embeddedCheckoutFrameRef.current) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== embeddedCheckoutFrameRef.current?.contentWindow) {
        return;
      }

      const payload = event.data;
      if (!payload || typeof payload !== 'object' || (payload as { source?: string }).source !== 'COR_PAYMENT') {
        return;
      }

      syncEmbeddedCheckoutHeight();

      if (window.self !== window.top) {
        window.parent.postMessage(payload, '*');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [successData?.checkoutUrl, syncEmbeddedCheckoutHeight]);

  useEffect(() => {
    if (!successData || !embeddedCheckoutUrl) {
      return;
    }

    const sync = () => syncEmbeddedCheckoutHeight();
    const intervalHandle = window.setInterval(sync, 1000);
    const timeoutHandles = [150, 600, 1500, 3000].map((delay) => window.setTimeout(sync, delay));

    sync();

    return () => {
      window.clearInterval(intervalHandle);
      timeoutHandles.forEach((handle) => window.clearTimeout(handle));
    };
  }, [embeddedCheckoutUrl, successData, syncEmbeddedCheckoutHeight]);

  useEffect(() => {
    if (!autoStartKey || successData || loading || providersLoading || setupIssue || amountIssue) {
      return;
    }

    if (!hasAvailableCheckoutPath) {
      return;
    }

    if (autoStartAttemptedKeyRef.current === autoStartKey) {
      return;
    }

    autoStartAttemptedKeyRef.current = autoStartKey;
    void createPaymentPage();
  }, [
    amountIssue,
    autoStartKey,
    createPaymentPage,
    hasAvailableCheckoutPath,
    loading,
    providersLoading,
    setupIssue,
    successData,
  ]);

  const iframeThemeStyle = useMemo(() => ({
    ['--iframe-bg' as string]: backgroundColor,
    ['--iframe-surface' as string]: surfaceColor,
    ['--iframe-text' as string]: fontColor,
    ['--iframe-accent' as string]: accentColor,
    ['--iframe-accent-strong' as string]: accentStrongColor,
    ['--iframe-accent-soft' as string]: accentSoftColor,
    ['--iframe-border' as string]: borderColor,
    ['--iframe-divider' as string]: dividerColor,
    ['--iframe-soft' as string]: softColor,
  } as React.CSSProperties), [
    accentColor,
    accentSoftColor,
    accentStrongColor,
    backgroundColor,
    borderColor,
    dividerColor,
    fontColor,
    softColor,
    surfaceColor,
  ]);

  return (
    <div
      className={`iframe-payment iframe-payment--${effectiveIframeTheme}${liveCheckoutMode ? ' iframe-payment--live' : ''}`}
      style={iframeThemeStyle}
    >
      <Container className="iframe-payment__container">
        <Card className="iframe-payment__panel">
          <Card.Body className="iframe-payment__body">
            {!successData && (
              <div className="iframe-payment__brand">
                <span className="iframe-payment__brand-mark">
                  <img src={RIZZPAY_LOGO_PUBLIC_PATH} alt="RizzPay" />
                </span>
                <span className="iframe-payment__brand-copy">
                  <strong>RizzPay</strong>
                  <small>Secure embedded checkout</small>
                </span>
              </div>
            )}

            {!successData && (
              <>
                <div className="iframe-payment__hero">
                  <div className="iframe-payment__hero-copy">
                    <h1 className="iframe-payment__title">{primaryHeading}</h1>
                  </div>
                </div>
              </>
            )}

            {setupIssue || amountIssue ? (
              <Alert variant="warning" className="mb-0">
                {setupIssue || amountIssue}
              </Alert>
            ) : null}

            {error ? (
              <Alert variant="danger" className="mb-0">
                {error}
              </Alert>
            ) : null}

            {successData ? (
              <section className="iframe-payment__section iframe-payment__section--checkout-live">
                <div className="iframe-payment__live-toolbar">
                  <div className="iframe-payment__live-toolbar-main">
                    <span className="iframe-payment__live-toolbar-label">
                      {shopMode ? 'Locked amount' : 'Amount ready'}
                    </span>
                    <strong className="iframe-payment__live-toolbar-value">
                      {hasEnteredAmount ? `${fiatCurrency} ${parsedAmount.toFixed(2)}` : 'Ready'}
                    </strong>
                  </div>
                  <div className="iframe-payment__workspace-actions">
                    {!shopMode ? (
                      <Button variant="outline-secondary" onClick={handleResetCheckout}>
                        Change amount
                      </Button>
                    ) : null}
                    {successLink ? (
                      <Button
                        as="a"
                        href={successLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outline-primary"
                      >
                        Open in new tab
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="iframe-payment__checkout-shell">
                  {!embeddedCheckoutLoaded ? (
                    <div className="iframe-payment__checkout-state">
                      <Spinner animation="border" size="sm" className="app-spinner" />
                      <span>Loading your checkout...</span>
                    </div>
                  ) : null}

                  {embeddedCheckoutUrl ? (
                    <iframe
                      ref={embeddedCheckoutFrameRef}
                      className="iframe-payment__checkout-frame"
                      title="Embedded payment checkout"
                      src={embeddedCheckoutUrl}
                      style={{ height: `${embeddedCheckoutHeight}px` }}
                      onLoad={() => {
                        setEmbeddedCheckoutLoaded(true);
                        syncEmbeddedCheckoutHeight();
                      }}
                    />
                  ) : null}
                </div>
              </section>
            ) : (
              <Form onSubmit={handleSubmit} className="iframe-payment__form">
                <section className="iframe-payment__section">
                  <div className="iframe-payment__section-header">
                    <div className="iframe-payment__section-title">
                      {shopMode ? 'Review the payment amount' : 'Enter the amount'}
                    </div>
                  </div>

                  <div className="iframe-payment__grid">
                    <Form.Group className="iframe-payment__field">
                      <Form.Label>Currency</Form.Label>
                      <div
                        ref={currencyDropdownRef}
                        className={`iframe-payment__currency-surface${showCurrencyDropdown ? ' iframe-payment__currency-surface--open' : ''}`}
                      >
                        <button
                          type="button"
                          className="iframe-payment__currency-trigger"
                          onClick={() => {
                            if (!shopMode) {
                              setShowCurrencyDropdown((current) => !current);
                              setCurrencySearchTerm('');
                            }
                          }}
                          aria-label="Fiat currency"
                          aria-haspopup="listbox"
                          aria-expanded={showCurrencyDropdown}
                          disabled={shopMode}
                        >
                          <img
                            src={selectedCurrencyOption.flagUrl}
                            alt=""
                            className="iframe-payment__currency-flag"
                            loading="lazy"
                          />
                          <span className="iframe-payment__currency-trigger-copy">
                            <strong>{selectedCurrencyOption.code}</strong>
                            <small>{selectedCurrencyOption.label}</small>
                          </span>
                          <span className="iframe-payment__currency-chevron" aria-hidden="true" />
                        </button>
                        {showCurrencyDropdown && !shopMode ? (
                          <div className="iframe-payment__currency-menu">
                            <Form.Control
                              type="text"
                              value={currencySearchTerm}
                              onChange={(e) => handleCurrencySearchChange(e.target.value)}
                              placeholder="Search currency or country"
                              className="iframe-payment__currency-search"
                              autoComplete="off"
                              autoFocus
                            />

                            {!currencySearchTerm.trim() && (
                              <div className="iframe-payment__currency-popular">
                                <div className="iframe-payment__currency-menu-label">Popular</div>
                                <div className="iframe-payment__currency-shortcuts">
                                  {popularCurrencyOptions.map((currency) => (
                                    <button
                                      key={currency.code}
                                      type="button"
                                      className={`iframe-payment__currency-shortcut${currency.code === fiatCurrency ? ' iframe-payment__currency-shortcut--active' : ''}`}
                                      onClick={() => handleCurrencySelect(currency.code)}
                                    >
                                      <img src={currency.flagUrl} alt="" loading="lazy" />
                                      <span>{currency.code}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="iframe-payment__currency-menu-label">All currencies</div>
                            <div className="iframe-payment__currency-list" role="listbox">
                              {filteredCurrencyOptions.length > 0 ? (
                                filteredCurrencyOptions.map((currency) => (
                                  <button
                                    key={currency.code}
                                    type="button"
                                    className={`iframe-payment__currency-option${currency.code === fiatCurrency ? ' iframe-payment__currency-option--active' : ''}`}
                                    onClick={() => handleCurrencySelect(currency.code)}
                                    role="option"
                                    aria-selected={currency.code === fiatCurrency}
                                  >
                                    <img
                                      src={currency.flagUrl}
                                      alt=""
                                      className="iframe-payment__currency-flag"
                                      loading="lazy"
                                    />
                                    <span className="iframe-payment__currency-option-copy">
                                      <strong>{currency.code}</strong>
                                      <small>{currency.label}</small>
                                    </span>
                                  </button>
                                ))
                              ) : (
                                <div className="iframe-payment__currency-empty">No currencies match this search.</div>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </Form.Group>

                    <Form.Group className="iframe-payment__field">
                      <Form.Label>Amount</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min={minimumAmount}
                        value={fiatAmount}
                        onChange={(e) => handleAmountChange(e.target.value)}
                        placeholder={minimumAmountLoading ? 'Checking minimum...' : `Enter amount (minimum ${exchangeRateService.formatMinimum(fiatCurrency, minimumAmount)})`}
                        className="iframe-payment__control"
                        disabled={shopMode}
                        readOnly={shopMode}
                      />
                      <div className={`iframe-payment__helper${isBelowMinimum ? ' iframe-payment__helper--warning' : ''}`}>
                        {shopMode
                          ? 'This amount is locked to match the merchant checkout.'
                          : minimumAmountLoading
                            ? 'Checking the minimum amount for this currency...'
                            : `Enter at least ${exchangeRateService.formatMinimum(fiatCurrency, minimumAmount)} to continue.`}
                      </div>
                    </Form.Group>
                  </div>
                </section>

                <div className="iframe-payment__submit-row">
                  <Button
                    type="submit"
                    disabled={loading || providersLoading || !hasAvailableCheckoutPath || Boolean(setupIssue) || Boolean(amountIssue)}
                    className="iframe-payment__submit-button"
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {shopMode ? 'Preparing checkout...' : 'Opening checkout...'}
                      </>
                    ) : shopMode ? (
                      'Open payment options'
                    ) : (
                      'Continue'
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default IframePaymentPage;
